import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MagnifyingGlassIcon, PrinterIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';

interface LabelItem {
  id: string;
  productName: string;
  sku: string;
  price: number;
  color?: string;
  size?: string;
  quantity: number;
}

const POSBarcodePrint: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [labelItems, setLabelItems] = useState<LabelItem[]>([]);
  const [labelSize, setLabelSize] = useState<'small' | 'medium'>('medium');
  const printRef = useRef<HTMLDivElement>(null);

  const fetchProducts = useCallback(async () => {
    try {
      const params: any = { per_page: 100 };
      if (search) params.search = search;
      const response = await apiService.get('/pos/products', { params });
      setProducts(response.data || []);
    } catch {}
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(fetchProducts, 300);
    return () => clearTimeout(timer);
  }, [fetchProducts]);

  const addLabel = (product: any, variant?: any, size?: any) => {
    const sku = variant?.sku || product.product_code;
    const id = `${product.id}-${variant?.id || 0}-${size?.name || ''}`;

    // Check if already in list
    if (labelItems.find(l => l.id === id)) {
      setLabelItems(prev => prev.map(l => l.id === id ? { ...l, quantity: l.quantity + 1 } : l));
      return;
    }

    setLabelItems(prev => [...prev, {
      id,
      productName: product.name,
      sku,
      price: size?.price || variant?.price || product.price,
      color: variant?.color,
      size: size?.name,
      quantity: 1,
    }]);
  };

  const updateQuantity = (id: string, qty: number) => {
    if (qty <= 0) {
      setLabelItems(prev => prev.filter(l => l.id !== id));
    } else {
      setLabelItems(prev => prev.map(l => l.id === id ? { ...l, quantity: qty } : l));
    }
  };

  const totalLabels = labelItems.reduce((sum, l) => sum + l.quantity, 0);

  const handlePrint = () => {
    if (!printRef.current || labelItems.length === 0) return;

    const labels = labelItems.flatMap(item =>
      Array(item.quantity).fill(null).map((_, i) => ({ ...item, _idx: i }))
    );

    const labelWidth = labelSize === 'small' ? '38mm' : '50mm';
    const labelHeight = labelSize === 'small' ? '25mm' : '30mm';
    const fontSize = labelSize === 'small' ? '7px' : '9px';
    const barcodeHeight = labelSize === 'small' ? '20px' : '28px';

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Barcode Labels</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; }
            .labels-container {
              display: flex;
              flex-wrap: wrap;
              gap: 2mm;
              padding: 3mm;
            }
            .label {
              width: ${labelWidth};
              height: ${labelHeight};
              border: 0.5px solid #ccc;
              padding: 2mm;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              align-items: center;
              text-align: center;
              overflow: hidden;
              page-break-inside: avoid;
            }
            .label-name {
              font-size: ${fontSize};
              font-weight: bold;
              line-height: 1.1;
              max-height: 2.2em;
              overflow: hidden;
              width: 100%;
            }
            .label-variant {
              font-size: ${labelSize === 'small' ? '6px' : '7px'};
              color: #666;
            }
            .label-barcode {
              font-family: 'Libre Barcode 128', 'Free 3 of 9', monospace;
              font-size: ${barcodeHeight};
              letter-spacing: -1px;
              line-height: 1;
            }
            .label-sku {
              font-size: ${labelSize === 'small' ? '6px' : '7px'};
              font-family: monospace;
            }
            .label-price {
              font-size: ${labelSize === 'small' ? '8px' : '10px'};
              font-weight: bold;
            }
            @media print {
              body { margin: 0; }
              .label { border: 0.3px solid #ddd; }
              @page { margin: 3mm; }
            }
          </style>
          <link href="https://fonts.googleapis.com/css2?family=Libre+Barcode+128&display=swap" rel="stylesheet">
        </head>
        <body>
          <div class="labels-container">
            ${labels.map(label => `
              <div class="label">
                <div class="label-name">${label.productName}</div>
                ${label.color || label.size ? `<div class="label-variant">${[label.color, label.size].filter(Boolean).join(' / ')}</div>` : ''}
                <div class="label-barcode">${label.sku}</div>
                <div class="label-sku">${label.sku}</div>
                <div class="label-price">${Number(label.price).toLocaleString()} MMK</div>
              </div>
            `).join('')}
          </div>
          <script>
            document.fonts.ready.then(function() {
              setTimeout(function() { window.print(); window.close(); }, 500);
            });
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Barcode Label Printing</h1>
          <p className="text-gray-600">Generate and print barcode labels for products</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={labelSize}
            onChange={(e) => setLabelSize(e.target.value as any)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          >
            <option value="small">Small (38×25mm)</option>
            <option value="medium">Medium (50×30mm)</option>
          </select>
          <button
            onClick={handlePrint}
            disabled={labelItems.length === 0}
            className="bg-indigo-600 text-white px-4 py-1.5 text-sm rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-1.5"
          >
            <PrinterIcon className="h-4 w-4" />
            <span>Print ({totalLabels} labels)</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Left: Product Selection */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-2.5" />
              <input
                type="text"
                placeholder="Search products..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
            {products.map((product: any) => (
              <div key={product.id} className="p-3 hover:bg-gray-50">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{product.name}</p>
                    <p className="text-xs text-gray-500">{product.product_code} • {Number(product.price).toLocaleString()} MMK</p>
                  </div>
                  {!product.has_variants && (
                    <button
                      onClick={() => addLabel(product)}
                      className="text-xs px-2 py-1 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100"
                    >
                      + Add
                    </button>
                  )}
                </div>
                {product.has_variants && product.variants?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {product.variants.map((variant: any) => (
                      <div key={variant.id}>
                        {variant.sizes?.map((size: any) => (
                          <div key={size.name} className="flex justify-between items-center ml-4 py-0.5">
                            <span className="text-xs text-gray-600">
                              {variant.color} / {size.name} — <span className="font-mono">{variant.sku}</span>
                            </span>
                            <button
                              onClick={() => addLabel(product, variant, size)}
                              className="text-xs px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100"
                            >
                              +
                            </button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Label Queue */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="font-medium text-gray-900">Print Queue ({totalLabels} labels)</h3>
            {labelItems.length > 0 && (
              <button onClick={() => setLabelItems([])} className="text-xs text-red-600 hover:text-red-800">Clear All</button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
            {labelItems.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">
                Click products to add to print queue
              </div>
            ) : (
              labelItems.map(item => (
                <div key={item.id} className="p-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                    <p className="text-xs text-gray-500">
                      {[item.color, item.size].filter(Boolean).join(' / ')} • {item.sku} • {Number(item.price).toLocaleString()} MMK
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-gray-600 hover:bg-gray-200">-</button>
                    <span className="text-sm font-medium w-6 text-center">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded text-gray-600 hover:bg-gray-200">+</button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Preview */}
          {labelItems.length > 0 && (
            <div className="p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500 mb-2">Label Preview:</p>
              <div ref={printRef} className="inline-block border border-gray-300 rounded p-2 bg-white text-center" style={{ width: labelSize === 'small' ? '38mm' : '50mm' }}>
                <p style={{ fontSize: '8px', fontWeight: 'bold' }}>{labelItems[0]?.productName}</p>
                {(labelItems[0]?.color || labelItems[0]?.size) && (
                  <p style={{ fontSize: '6px', color: '#666' }}>{[labelItems[0]?.color, labelItems[0]?.size].filter(Boolean).join(' / ')}</p>
                )}
                <p style={{ fontFamily: "'Libre Barcode 128', monospace", fontSize: '24px', lineHeight: 1 }}>{labelItems[0]?.sku}</p>
                <p style={{ fontSize: '6px', fontFamily: 'monospace' }}>{labelItems[0]?.sku}</p>
                <p style={{ fontSize: '9px', fontWeight: 'bold' }}>{Number(labelItems[0]?.price || 0).toLocaleString()} MMK</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default POSBarcodePrint;
